// The words for a job as one line item for one period, a quote as a bundle of jobs and an invoice as a collection of them
// (finance-real, the user 2026-10-22): a job's rhythm, its status, when its series makes the next one, a quote's status,
// an invoice's. Copy only: every figure is the server's (TD-102).
import type { BadgeTone } from "@/components/ui/Badge";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import type { JobInvoice, JobInvoiceStatus, LineJob, LineJobStatus, QuoteBundleStatus, TeamInvoiceStatus } from "../types";
import { dateLabel, pctText } from "../utils/format";
import { invoiceName, rhythmText } from "../utils/names";

const postsWord = ENTITY_CONFIG.post.plural.toLowerCase();
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();

/** How a job bills (DD-061), names.ts's, so a job's subline and its words agree. */
export { rhythmText };

/** A job's list price and the discount it implies, the server's, beside its price: "List 800,00 € · 15% off"; null with
 * no list price. */
export const listPriceText = (job: Pick<LineJob, "listPriceMinor" | "discountPct">): string | null =>
  job.listPriceMinor === null || job.discountPct === null ? null : `List ${formatMoney(job.listPriceMinor, "EUR")} · ${pctText(job.discountPct)}% off`;

export const JOB_STATUSES: LineJobStatus[] = ["draft", "quoting", "agreed", "active", "paused", "done", "invoiced", "dropped"];

/** A status's pill: its Badge tone, and the same tone's classes for the status dropdown (StatusBadge, OptionDropdown). */
export interface StatusLook {
  label: string;
  tone: BadgeTone;
  bg: string;
  text: string;
  dot: string;
}

const TONE_CLASSES: Record<BadgeTone, Omit<StatusLook, "label" | "tone">> = {
  gray: { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-400" },
  amber: { bg: "bg-amber-50", text: "text-amber-800", dot: "bg-amber-500" },
  green: { bg: "bg-green-50", text: "text-green-700", dot: "bg-green-500" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-500" },
};

const look = (label: string, tone: BadgeTone): StatusLook => ({ label, tone, ...TONE_CLASSES[tone] });

export const JOB_STATUS: Record<LineJobStatus, StatusLook> = {
  draft: look("Draft", "gray"),
  quoting: look("Quoting", "amber"),
  agreed: look("Agreed", "indigo"),
  active: look("Active", "green"),
  paused: look("Paused", "purple"),
  done: look("Done", "teal"),
  invoiced: look("Invoiced", "blue"),
  dropped: look("Dropped", "red"),
};

/** When the series makes its next job, or that it stops here. Null for a one-off and a job not under way; a done job's
 * series goes on as an active one's does. */
export function nextText(job: LineJob): string | null {
  if (job.kind === "one_time") return null;
  if (job.status === "paused") return `Paused: no next ${jobWord} is made, and no ${ENTITY_CONFIG.invoice.label.toLowerCase()} takes it, until it is active again.`;
  if (job.status !== "active" && job.status !== "done" && job.status !== "invoiced") return null;
  if (job.seriesEnded) return `Its series has ended: no ${jobWord} follows this one.`;
  if (!job.next) return null;
  if (job.next.on === null) return `The next run's ${jobWord} is made once this run's ${postsWord} are out.`;
  return `The ${job.next.label} ${jobWord} is made on ${dateLabel(job.next.on)}, a copy of this one.`;
}

/** A quote's statuses, in the order its tabs and its dropdown list them. */
export const QUOTE_STATUSES: QuoteBundleStatus[] = ["draft", "proposed", "accepted", "declined"];

export const QUOTE_STATUS: Record<QuoteBundleStatus, StatusLook> = {
  draft: look("Draft", "gray"),
  proposed: look("Proposed", "amber"),
  accepted: look("Accepted", "green"),
  declined: look("Declined", "red"),
};

/** An invoice's statuses, in the order its tabs list them (DD-062). Overdue is the server's, from the invoice's date: its
 * dropdown never offers it. */
export const JOB_INVOICE_STATUSES: JobInvoiceStatus[] = ["draft", "sent", "overdue", "partial", "paid"];

/** One tone per status across every finance list (the user 2026-10-23): Draft is gray as a job's and a quote's are, Sent
 * blue, Overdue red, Partly paid amber, Paid green. */
export const JOB_INVOICE_STATES: Record<JobInvoiceStatus, StatusLook> = {
  draft: look("Draft", "gray"),
  sent: look("Sent", "blue"),
  overdue: look("Overdue", "red"),
  partial: look("Partly paid", "amber"),
  paid: look("Paid", "green"),
};

/** A team invoice's statuses (the user 2026-10-22): Draft, Received once their invoice arrives, Paid once paid out. */
export const TEAM_INVOICE_STATUSES: TeamInvoiceStatus[] = ["draft", "received", "paid"];

/** Draft and Paid read as a client invoice's; Received takes Sent's tone, as it lists under Sent or received. */
export const TEAM_INVOICE_STATES: Record<TeamInvoiceStatus, StatusLook> = {
  draft: JOB_INVOICE_STATES.draft,
  received: look("Received", JOB_INVOICE_STATES.sent.tone),
  paid: JOB_INVOICE_STATES.paid,
};

/** A client invoice's name (`invoiceName`): "Draft invoice", or "Invoice INV2600048". */
export const jobInvoiceTitle = (i: Pick<JobInvoice, "status" | "number" | "sentOn">): string => invoiceName({ kind: "client", ...i });
